import { Policy } from "../../../api/v3/policy";
import { formatCurrency } from "../../../lib/currencyUtil";
import { isNotUndefined } from "../../../typeguards/isNotUndefined";
import { getInsuredsLabels } from "../PolicyDetails.helpers";
import { PolicyCoverageData, PolicyDetailsHeader } from "../PolicyDetails.state";

const DEFAULT_FALLBACK_TEXT_PAYMENT_PERIOD = "maand";
const DEFAULT_FALLBACK_TEXT = "";
const NEW_LINE = "\n";

const translateInsuranceType = (type: string | undefined): string => {
  switch (type?.toUpperCase()) {
    case "LEVEL":
      return "Gelijkblijvend";
    case "LINEAR":
      return "Lineair dalend";
    case "ANNUITY":
      return "Annuïtair dalend";
    default:
      return "";
  }
};

export const getPolicyDetailsHeader = (policy: Policy, dictionary: Record<string, string>): PolicyDetailsHeader => ({
  policyNumber: policy.policyNumber,
  productName: policy.productName,
  title: dictionary[`insuranceType.${policy.productCode}`] ?? policy.productCode,
  insuranceTakers: policy.policyHolders?.map(({ givenName, prefixName, surName }) =>
    [givenName, prefixName, surName].filter(Boolean).join(" ")
  ),
});

export const getPolicyDetails = (policy: Policy): { key: string; value: string }[] => {
  return [
    {
      key: "Ingangsdatum",
      value: policy.effectiveDate,
    },
  ];
};

const translatePaymentPeriod = (paymentPeriod: string) => {
  const lowerCasePaymentPeriod = paymentPeriod.toLowerCase();

  if (lowerCasePaymentPeriod === "koopsom") {
    return "Eenmalig";
  }

  return paymentPeriod;
};

export const getPaymentTerm = (policy: Policy) =>
  policy.premiums?.map((p) => translatePaymentPeriod(p.paymentPeriod)).join(NEW_LINE) ??
  DEFAULT_FALLBACK_TEXT_PAYMENT_PERIOD;

export const getTotalPremium = (policy: Policy) =>
  policy.premiums?.map((p) => formatCurrency(p.amount)).join(NEW_LINE) || formatCurrency(0);

export const getPolicyCoverage = (policy: Policy) =>
  policy?.covers?.map((c) => {
    const insureds = c.insuredObjects
      .map((insuredObject) => {
        const insured = insuredObject.insured;

        if (!insured) {
          return undefined;
        }

        return {
          ...insured,
        };
      })
      .filter(isNotUndefined);

    const insuredsData = insureds
      .map(({ givenName, prefixName, surName, dateOfBirth }) =>
        [givenName, prefixName, surName].filter(Boolean).join(" ").concat(` (${dateOfBirth})`)
      )
      .join(NEW_LINE);

    const formatWithPaymentTerm = (baseText: string, paymentTerm: string | undefined, suffix = "") =>
      paymentTerm ? `${baseText} per ${paymentTerm.toLowerCase()}${suffix}` : baseText;

    const paymentTerm = getPaymentTerm(policy)?.toLowerCase();
    const premiumText = formatWithPaymentTerm(formatCurrency(c.premium ?? 0), paymentTerm, " excl. kosten");

    return {
      coverageData: [
        { key: getInsuredsLabels(insureds.length), value: insuredsData },
        { key: "Verzekeringsvorm", value: translateInsuranceType(c.insuranceType) },
        {
          key: "Verzekerd bedrag",
          value: c.insuredAmount ? formatCurrency(c.insuredAmount) : DEFAULT_FALLBACK_TEXT,
        },
        { key: "Einddatum dekking", value: c.expiredOnDate ?? DEFAULT_FALLBACK_TEXT },
        {
          key: "Premie",
          value: premiumText,
        },
      ].filter(isNotUndefined),
    };
  }) ?? [];

export const getPolicyCoverageData = (policy: Policy): PolicyCoverageData | null => ({
  paymentTerm: `Totale premie per ${getPaymentTerm(policy).toLowerCase()}`,
  totalPremium: `${getTotalPremium(policy)} incl. kosten`,
  covers: getPolicyCoverage(policy),
});
